import React from 'react';
import { styled } from '@compiled/react';
import type NotificationDirectIcon from '@atlaskit/icon/glyph/notification-direct';
import { Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

export const IconBoxColor = {
	Blue: 'blue',
	Gray: 'gray',
	Green: 'green',
	Lime: 'lime',
	Orange: 'orange',
	Purple: 'purple',
	Red: 'red',
	Teal: 'teal',
	Yellow: 'yellow',
	Magenta: 'magenta',
} as const;

type IconBoxProps = {
	// eslint-disable-next-line @atlaskit/design-system/no-legacy-icons
	icon: typeof NotificationDirectIcon;
	iconSize?: 'small' | 'medium' | 'large';
	color?: (typeof IconBoxColor)[keyof typeof IconBoxColor];
	withCustomContainer?: boolean;
};

export const IconBox = ({
	color,
	icon,
	iconSize = 'medium',
	withCustomContainer,
}: IconBoxProps) => {
	const getColorStyle = () => {
		switch (color) {
			case 'gray':
				return { xcss: grayStyles, color: token('color.icon.accent.gray', '#758195') };
			case 'green':
				return { xcss: greenStyles, color: token('color.icon.accent.green', '#00875A') };
			case 'lime':
				return { xcss: limeStyles, color: token('color.icon.accent.lime', '#6A9A23') };
			case 'orange':
				return { xcss: orangeStyles, color: token('color.icon.accent.orange', '#FF991F') };
			case 'purple':
				return { xcss: purpleStyles, color: token('color.icon.accent.purple', '#6554C0') };
			case 'red':
				return { xcss: redStyles, color: token('color.icon.accent.red', '#FF5630') };
			case 'teal':
				return { xcss: tealStyles, color: token('color.icon.accent.teal', '#00A3BF') };
			case 'yellow':
				return { xcss: yellowStyles, color: token('color.icon.accent.yellow', '#FFE380') };
			case 'magenta':
				return {
					xcss: magentaStyles,
					color: token('color.icon.accent.magenta', '#CD519D'),
				};
			default:
				return { xcss: blueStyles, color: token('color.icon.accent.blue', '#4C9AFF') };
		}
	};

	const Icon = icon;

	if (withCustomContainer) {
		return (
			<Flex xcss={[boxStyles, getColorStyle().xcss]} alignItems="center" justifyContent="center">
				<IconContainer>
					<Icon size={iconSize} primaryColor={getColorStyle().color} label="" />
				</IconContainer>
			</Flex>
		);
	}

	return (
		<Flex xcss={[boxStyles, getColorStyle().xcss]} alignItems="center" justifyContent="center">
			<Icon size={iconSize} primaryColor={getColorStyle().color} label="" />
		</Flex>
	);
};

const boxStyles = xcss({
	borderRadius: 'border.radius.100',
	flexShrink: 0,
	width: 'size.200',
	height: 'size.200',
});
const blueStyles = xcss({ backgroundColor: 'color.background.accent.blue.subtlest' });
const grayStyles = xcss({ backgroundColor: 'color.background.accent.gray.subtlest' });
const greenStyles = xcss({ backgroundColor: 'color.background.accent.green.subtlest' });
const limeStyles = xcss({ backgroundColor: 'color.background.accent.lime.subtlest' });
const orangeStyles = xcss({ backgroundColor: 'color.background.accent.orange.subtlest' });
const purpleStyles = xcss({ backgroundColor: 'color.background.accent.purple.subtlest' });
const redStyles = xcss({ backgroundColor: 'color.background.accent.red.subtlest' });
const tealStyles = xcss({ backgroundColor: 'color.background.accent.teal.subtlest' });
const yellowStyles = xcss({ backgroundColor: 'color.background.accent.yellow.subtlest' });
const magentaStyles = xcss({ backgroundColor: 'color.background.accent.magenta.subtlest' });

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const IconContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '20px',
		height: '20px',
	},
});
